import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Navbar from "../../components/navbar/Navbar";
import Review from "./Review";
import GallerySection from "./GallerySection";
import Contact from "./Contact";
import WhatsHappening from "./WhatsHappening";
import Awards from "./Awards";
import Homeservices from "./Homeservices";
import MainBenner from "./MainBenner";
import AboutHome from "./AboutHome";
import Affliations from "./Affliations";
import CookieBanner from "../../components/cookie/CookieBanner";
import SmoothTransition from "../../components/animations/SmoothTransition";
import SmoothScroll from "../../components/animations/SmoothScroll";
import ParallaxElement from "../../components/animations/ParallaxElement";

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

const Home = () => {
  const pageRef = useRef(null);
  const sectionRefs = useRef([]);

  useEffect(() => {
    // Create smooth page entrance animation
    const pageTl = gsap.timeline();

    // Set initial state for all sections
    gsap.set(sectionRefs.current, {
      opacity: 0,
      y: 50,
      scale: 0.95,
    });

    // Animate page entrance
    pageTl.to(pageRef.current, {
      opacity: 1,
      duration: 0.5,
      ease: "power2.out",
    });

    // Create smooth section transitions
    sectionRefs.current.forEach((section, index) => {
      if (section) {
        gsap.to(section, {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 1.2,
          ease: "power3.out",
          delay: index * 0.1, // Staggered entrance
          scrollTrigger: {
            trigger: section,
            start: "top 85%",
            end: "top 20%",
            toggleActions: "play none none reverse",
            scrub: false,
          },
        });
      }
    });

    // Add smooth scroll behavior
    gsap.registerPlugin(ScrollTrigger);

    // Create smooth scroll effect
    ScrollTrigger.create({
      trigger: "body",
      start: "top top",
      end: "bottom bottom",
      onUpdate: (self) => {
        // Add smooth momentum to scroll
        gsap.to(window, {
          duration: 0.3,
          ease: "power2.out",
        });
      },
    });

    // Cleanup function
    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <SmoothScroll>
      <div
        ref={pageRef}
        className="font-poppins overflow-x-hidden bg-bgWhite opacity-0 no-scrollbar"
      >
        <CookieBanner />
        <Navbar />

        {/* Hero Section with enhanced animations */}
        <SmoothTransition animationType="fadeUp" delay={0.2} duration={1.5}>
          <div ref={(el) => (sectionRefs.current[0] = el)}>
            <MainBenner />
          </div>
        </SmoothTransition>

        {/* About Section with slide animation */}
        <SmoothTransition animationType="slideUp" delay={0.3} duration={1.8}>
          <div ref={(el) => (sectionRefs.current[1] = el)}>
            <AboutHome />
          </div>
        </SmoothTransition>

        {/* Affiliations with scale animation */}
        <SmoothTransition animationType="scale" delay={0.1} duration={1.2}>
          <div ref={(el) => (sectionRefs.current[2] = el)}>
            <Affliations />
          </div>
        </SmoothTransition>

        {/* Services with staggered fade */}
        <SmoothTransition animationType="fadeUp" delay={0.4} duration={1.6}>
          <div ref={(el) => (sectionRefs.current[3] = el)}>
            <Homeservices />
          </div>
        </SmoothTransition>

        {/* Awards with bounce effect */}
        <SmoothTransition animationType="bounce" delay={0.2} duration={1.4}>
          <div ref={(el) => (sectionRefs.current[4] = el)}>
            <Awards />
          </div>
        </SmoothTransition>

        {/* Reviews with fade left */}
        <SmoothTransition animationType="fadeLeft" delay={0.3} duration={1.5}>
          <div ref={(el) => (sectionRefs.current[5] = el)}>
            <Review />
          </div>
        </SmoothTransition>

        {/* News with fade right */}
        <SmoothTransition animationType="fadeRight" delay={0.2} duration={1.3}>
          <div ref={(el) => (sectionRefs.current[6] = el)}>
            <WhatsHappening />
          </div>
        </SmoothTransition>

        {/* Gallery with parallax effect */}
        <ParallaxElement speed={0.3} direction="up">
          <SmoothTransition animationType="fadeUp" delay={0.5} duration={1.7}>
            <div
              ref={(el) => (sectionRefs.current[7] = el)}
              className="xl:mt-8"
            >
              <GallerySection />
            </div>
          </SmoothTransition>
        </ParallaxElement>

        {/* Contact with final fade up */}
        <SmoothTransition animationType="fadeUp" delay={0.4} duration={1.8}>
          <div ref={(el) => (sectionRefs.current[8] = el)}>
            <Contact />
          </div>
        </SmoothTransition>
      </div>
    </SmoothScroll>
  );
};

export default Home;
